import { useEffect, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingSpotDesiredPriceQuerySchema } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import { previewBiddingSpotBidFeeAdjustedPrices } from '@ebalance/proto/bffuser/v1/bidding_spot_service-BiddingSpotService_connectquery'
import { PreviewBiddingSpotBidFeeAdjustedPricesRequestSchema } from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'
import { skipToken } from '@tanstack/react-query'
import type { TFunction } from 'i18next'

import {
  type FeeAutoConsiderConfirmed,
  type FeeAutoConsiderPreview,
  previewQueryStatus,
} from '~/models/feeAutoConsiderPreview'
import {
  type SpotFeeAdjustedPriceInput,
  useSpotFeeAutoConsiderPreviews,
} from '~/pages/SpotBidding/hooks/useSpotFeeAutoConsiderPreview'
import { resolveSpotFeeSubmission } from '~/pages/SpotBidding/models/feePreview'
import {
  type SpotFeeAutoConsider,
  isSpotFeeAutoConsiderPending,
} from '~/pages/SpotBidding/models/spotFeeAutoConsider'

/** 保存済みの札を開いた時点の札。 手数料自動考慮の逆算の入力になる。 */
export type SpotFeeEditSeed = {
  direction: Side
  /** 保存済みの入札価格 (Decimal string)。 */
  orderPriceYenPerKwh: string
}

/**
 * 保存済みの札を開いたモーダルの価格欄の初期値 (AC-BSP-001a / AC-BSP-004b / AC-BSP-129)。
 *
 * - `none`: 逆算しない (新規作成・成行)
 * - `pending`: 扱いか逆算の応答を待っている。 価格欄は触れず、 保存もできない
 * - `asOpened`: 開いた時の扱い (OFF・単価を使えない) のまま。 初期値は保存済みの入札価格
 * - `unavailable`: ON だが逆算を得られなかった。 閉じるまで OFF として扱い、 初期値は保存済みの入札価格
 * - `fee`: ON。 初期値は backend が逆算した希望価格
 */
type SpotFeeEditBaseline =
  | { kind: 'none' }
  | { kind: 'pending' }
  | { kind: 'asOpened' }
  | { kind: 'unavailable' }
  | { kind: 'fee'; initialDesiredPricesYenPerKwh: string[] }

function useSpotFeeEditBaseline({
  isOpen,
  seeds,
  fee,
}: {
  isOpen: boolean
  /** 保存済みの札 (指値) を開いた時だけ渡す。 */
  seeds: readonly SpotFeeEditSeed[] | undefined
  /** 開いているモーダルの扱い ([[useSpotFeeAutoConsider]])。 */
  fee: SpotFeeAutoConsider
}): SpotFeeEditBaseline {
  const [pinned, setPinned] = useState<
    { seeds: readonly SpotFeeEditSeed[]; asksDesiredPrice: boolean } | undefined
  >(undefined)
  const [settled, setSettled] = useState<SpotFeeEditBaseline | undefined>(
    undefined,
  )
  if (!isOpen && (pinned !== undefined || settled !== undefined)) {
    setPinned(undefined)
    setSettled(undefined)
  }
  const canPin =
    isOpen && seeds !== undefined && !isSpotFeeAutoConsiderPending(fee)
  if (pinned === undefined && canPin) {
    setPinned({ seeds, asksDesiredPrice: fee.kind === 'on' })
  }

  const asks = pinned?.asksDesiredPrice === true && settled === undefined
  const { data, isPending, isError, failureCount } = useQuery(
    previewBiddingSpotBidFeeAdjustedPrices,
    !asks
      ? skipToken
      : create(PreviewBiddingSpotBidFeeAdjustedPricesRequestSchema, {
          desiredPriceQueries: pinned.seeds.map((seed) =>
            create(BiddingSpotDesiredPriceQuerySchema, {
              direction: seed.direction,
              orderPriceYenPerKwh: { value: seed.orderPriceYenPerKwh },
            }),
          ),
        }),
    // 開くたびに取り直す: 前に開いた時の逆算の応答を cache から出さない。
    { throwOnError: false, gcTime: 0 },
  )
  const live = ((): SpotFeeEditBaseline | undefined => {
    if (pinned === undefined) return undefined
    if (!pinned.asksDesiredPrice) return { kind: 'asOpened' }
    const status = previewQueryStatus({
      waiting: isPending,
      isError,
      failureCount,
      hasData: data !== undefined,
    })
    if (status === 'loading') return { kind: 'pending' }
    const desired = pinned.seeds.map(
      (_, i) => data?.desiredPriceResults[i]?.desiredPriceYenPerKwh?.value,
    )
    return status === 'success' &&
      desired.every((d): d is string => d !== undefined)
      ? { kind: 'fee', initialDesiredPricesYenPerKwh: desired }
      : { kind: 'unavailable' }
  })()
  if (settled === undefined && live !== undefined && live.kind !== 'pending') {
    setSettled(live)
  }
  if (!isOpen || seeds === undefined) return { kind: 'none' }
  return settled ?? live ?? { kind: 'pending' }
}

export type SpotFeePriceEntry<K extends string> = {
  key: K
  seed?: SpotFeeEditSeed
  input: SpotFeeAdjustedPriceInput | undefined
  isMarket: boolean
}

type SubmittedPrice<K extends string> = {
  key: K
  price: string | number | undefined
  isMarket: boolean
}

type PriceEntriesSubmission<K extends string> =
  | { kind: 'hold' }
  | { kind: 'error'; key: K; message: string }
  | { kind: 'send'; confirmed: Partial<Record<K, FeeAutoConsiderConfirmed>> }

export type SpotFeePriceEntriesState<K extends string> = {
  fee: SpotFeeAutoConsider
  previews: Partial<Record<K, FeeAutoConsiderPreview>>
  acceptsDesiredPriceInput: boolean
  isPriceEntryPending: boolean
  isSubmissionBlocking: boolean
  resolveSubmission: (
    t: TFunction,
    rows: ReadonlyArray<SubmittedPrice<K>>,
  ) => PriceEntriesSubmission<K>
}

/**
 * 保存済み価格の逆算、未編集欄への反映、プレビューと全行の保存可否を所有する。
 * フォームの初期化effectの後に呼び、価格欄だけのread/writeを渡す。
 * restoreKeyは、同じ札のままフォームの初期化方法が変わる場合の識別子。
 */
export function useSpotFeePriceEntries<K extends string>({
  isOpen,
  fee: openedFee,
  isSettingsPending,
  entries,
  readPrice,
  writePrice,
  restoreKey,
  pendingPresentation = 'loading',
}: {
  isOpen: boolean
  fee: SpotFeeAutoConsider
  isSettingsPending: boolean
  entries: ReadonlyArray<SpotFeePriceEntry<K>>
  readPrice: (key: K) => number | undefined
  writePrice: (key: K, price: number) => void
  restoreKey?: string
  pendingPresentation?: 'keepPriceLabel' | 'loading'
}): SpotFeePriceEntriesState<K> {
  const seededEntries = entries.filter((entry) => entry.seed !== undefined)
  const baseline = useSpotFeeEditBaseline({
    isOpen,
    seeds:
      seededEntries.length === 0
        ? undefined
        : seededEntries.flatMap((entry) =>
            entry.seed === undefined ? [] : [entry.seed],
          ),
    fee: openedFee,
  })
  const desiredFee: SpotFeeAutoConsider =
    baseline.kind === 'pending'
      ? { kind: 'loading' }
      : baseline.kind === 'unavailable'
        ? { kind: 'fallbackOff' }
        : openedFee
  const applicationKey =
    baseline.kind === 'fee'
      ? JSON.stringify([
          restoreKey,
          seededEntries.map((entry, i) => [
            entry.key,
            entry.seed?.orderPriceYenPerKwh,
            baseline.initialDesiredPricesYenPerKwh[i],
          ]),
        ])
      : undefined
  const [appliedKey, setAppliedKey] = useState<string | undefined>(undefined)
  if (!isOpen && appliedKey !== undefined) setAppliedKey(undefined)
  const isApplying =
    applicationKey !== undefined && appliedKey !== applicationKey
  useEffect(() => {
    if (!isOpen || baseline.kind !== 'fee' || !isApplying) return
    for (const [i, entry] of seededEntries.entries()) {
      const desired = baseline.initialDesiredPricesYenPerKwh[i]
      if (entry.seed === undefined || desired === undefined) continue
      const current = readPrice(entry.key)
      if (
        current === Number(entry.seed.orderPriceYenPerKwh) &&
        current !== Number(desired)
      ) {
        writePrice(entry.key, Number(desired))
      }
    }
    setAppliedKey(applicationKey)
  }, [
    isOpen,
    baseline,
    isApplying,
    seededEntries,
    readPrice,
    writePrice,
    applicationKey,
  ])

  const isPriceEntryPending = isSpotFeeAutoConsiderPending(desiredFee)
  const isPreviewPending = isPriceEntryPending || isApplying
  const state = useSpotFeeAutoConsiderPreviews({
    isOpen,
    fee: desiredFee,
    enabled: !isPreviewPending,
    inputs: entries.map((entry) => (entry.isMarket ? undefined : entry.input)),
  })
  const previews: Partial<Record<K, FeeAutoConsiderPreview>> = {}
  for (const [i, entry] of entries.entries()) {
    previews[entry.key] = state.previews[i]
  }
  const isSubmissionBlocking =
    isSettingsPending ||
    isPreviewPending ||
    entries.some(
      (entry) => !entry.isMarket && previews[entry.key]?.kind === 'blocked',
    )

  return {
    fee:
      baseline.kind === 'pending' && pendingPresentation === 'keepPriceLabel'
        ? openedFee
        : state.fee,
    previews,
    acceptsDesiredPriceInput: desiredFee.kind === 'on',
    isPriceEntryPending,
    isSubmissionBlocking,
    resolveSubmission(t, rows) {
      if (isSettingsPending || isPreviewPending) return { kind: 'hold' }
      const confirmed: Partial<Record<K, FeeAutoConsiderConfirmed>> = {}
      for (const row of rows) {
        const entry = entries.find((entry) => entry.key === row.key)
        const submission =
          !row.isMarket && entry?.input === undefined
            ? {
                kind: 'error' as const,
                message: t('page:biddingCommon.validation.priceRange'),
              }
            : resolveSpotFeeSubmission(t, {
                price: row.price,
                isMarket: row.isMarket,
                fee: state.fee,
                preview: previews[row.key] ?? {
                  kind: 'idle',
                  hint: 'incomplete',
                },
                rejectedByBackend: state.invalidInput,
              })
        if (submission.kind === 'error') return { ...submission, key: row.key }
        if (submission.kind === 'hold') return submission
        if (submission.feeAutoConsider !== undefined)
          confirmed[row.key] = submission.feeAutoConsider
      }
      return { kind: 'send', confirmed }
    },
  }
}
